import logger from './logger';
const DEFAULT_OPTIONS = {
    backgroundColor: '#000000',
    height: 600,
    overlayContainerId: '',
    title: 'Presentation Window',
    width: 960
};
const WINDOW_FEATURES_BASE = 'menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no';
const WINDOW_NAME = 'PresentationWindow';
/**
 * Manages a popup window that renders a MediaStream as a presentation view.
 * Handles window lifecycle, video rendering, and resource cleanup.
 */
export class PresentationWindow {
    /**
   * Creates a new PresentationWindow instance.
   */
    constructor() {
        this._eventListeners = new Map();
        this._options = Object.assign({}, DEFAULT_OPTIONS);
        this._popupWindow = null;
        this._stream = null;
        this._trackEndedHandler = null;
        this._videoElement = null;
    }
    /**
   * Opens a presentation window and renders the provided MediaStream.
   * If a window is already open, it closes the previous one first.
   *
   * @param {MediaStream} stream - The MediaStream containing video tracks to render.
   * @param {IPresentationWindowOptions} [options] - Configuration options for the window.
   * @returns {Window | null} The opened window reference, or null if blocked/failed.
   */
    open(stream, options) {
        if (!stream || !(stream instanceof MediaStream)) {
            logger.error('open: Invalid or null MediaStream provided.');
            return null;
        }
        const videoTracks = stream.getVideoTracks();
        if (videoTracks.length === 0) {
            logger.error('open: MediaStream contains no video tracks.');
            return null;
        }
        if (this._popupWindow && !this._popupWindow.closed) {
            this.close();
        }
        this._options = Object.assign(Object.assign({}, DEFAULT_OPTIONS), options);
        this._stream = stream;
        const { width, height, title, backgroundColor } = this._options;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        const features = `${WINDOW_FEATURES_BASE},width=${width},height=${height},left=${left},top=${top}`;
        try {
            this._popupWindow = window.open('', WINDOW_NAME, features);
        }
        catch (e) {
            logger.error('open: Exception thrown while opening popup window:', e);
            this._popupWindow = null;
        }
        if (!this._popupWindow || this._popupWindow.closed) {
            logger.error('open: Popup blocked or failed to open.');
            return null;
        }
        this._buildWindowContent(this._popupWindow, title, backgroundColor);
        this._attachVideo(this._popupWindow, stream);
        this._attachOverlay(this._popupWindow);
        this._listenForTrackEnd(videoTracks);
        this._listenForWindowClose();
        this._emit('open', this._popupWindow);
        return this._popupWindow;
    }
    /**
   * Closes the presentation window and cleans up all resources.
   */
    close() {
        if (this._popupWindow && !this._popupWindow.closed) {
            this._popupWindow.close();
        }
        this._cleanup();
    }
    /**
   * Registers a callback for the 'close' event.
   *
   * @param {PresentationWindowEventCallback<void>} callback - Called when the window is closed.
   */
    onWindowClosed(callback) {
        this.on('close', callback);
    }
    /**
   * Registers a callback for the 'open' event.
   *
   * @param {PresentationWindowEventCallback<Window>} callback - Called when the window is opened.
   */
    onWindowOpened(callback) {
        this.on('open', callback);
    }
    /**
   * Registers a callback for a specific event.
   *
   * @param {string} event - The event name.
   * @param {Function} callback - The callback function.
   */
    on(event, callback) {
        if (!this._eventListeners.has(event)) {
            this._eventListeners.set(event, new Set());
        }
        this._eventListeners
            .get(event)
            .add(callback);
    }
    /**
   * Unregisters a callback for a specific event.
   *
   * @param {string} event - The event name.
   * @param {Function} callback - The callback function to remove.
   */
    off(event, callback) {
        var _a;
        (_a = this._eventListeners
            .get(event)) === null || _a === void 0 ? void 0 : _a.delete(callback);
    }
    /**
   * Returns whether the presentation window is currently open.
   */
    get isOpen() {
        return this._popupWindow !== null && !this._popupWindow.closed;
    }
    /**
   * Returns the underlying Window reference, or null.
   */
    get window() {
        return this._popupWindow;
    }
    /**
   * Writes the HTML document structure into the popup window.
   */
    _buildWindowContent(win, title, backgroundColor) {
        const doc = win.document;
        doc.open();
        doc.write(`<!DOCTYPE html>
<html>
<head>
    <title>${this._escapeHtml(title)}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; height: 100%; overflow: hidden; background: ${backgroundColor}; }
        #video-container {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
        }
        #presentation-video {
            width: 100%;
            height: 100%;
            object-fit: contain;
            background: ${backgroundColor};
        }
        #overlay-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
        }
        #overlay-container > * { pointer-events: auto; }
    </style>
</head>
<body>
    <div id="video-container">
        <video id="presentation-video" autoplay playsinline muted></video>
        <div id="overlay-container"></div>
    </div>
</body>
</html>`);
        doc.close();
    }
    /**
   * Attaches the MediaStream to the video element in the popup window.
   */
    _attachVideo(win, stream) {
        const video = win.document.getElementById('presentation-video');
        if (!video) {
            logger.error('_attachVideo: Video element not found in popup window.');
            return;
        }
        this._videoElement = video;
        video.srcObject = stream;
    }
    /**
   * Clones and injects a custom overlay container into the popup window.
   */
    _attachOverlay(win) {
        const { overlayContainerId } = this._options;
        if (!overlayContainerId) {
            return;
        }
        const overlayEl = win.document.getElementById('overlay-container');
        if (!overlayEl) {
            return;
        }
        const customContainer = document.getElementById(overlayContainerId);
        if (customContainer) {
            const clone = customContainer.cloneNode(true);
            clone.style.pointerEvents = 'auto';
            overlayEl.appendChild(clone);
        }
    }
    /**
   * Listens for video track end events to auto-close the window.
   */
    _listenForTrackEnd(videoTracks) {
        this._trackEndedHandler = () => {
            this.close();
        };
        for (const track of videoTracks) {
            track.addEventListener('ended', this._trackEndedHandler);
        }
    }
    /**
   * Polls the popup window to detect when the user closes it manually.
   */
    _listenForWindowClose() {
        if (!this._popupWindow) {
            return;
        }
        const checkInterval = this._popupWindow.setInterval(() => {
            var _a;
            if ((_a = this._popupWindow) === null || _a === void 0 ? void 0 : _a.closed) {
                this._popupWindow.clearInterval(checkInterval);
                this._onWindowClosedExternally();
            }
        }, 250);
    }
    /**
   * Handles cleanup and emits the close event when window is closed externally.
   */
    _onWindowClosedExternally() {
        this._cleanup();
        this._emit('close', undefined);
    }
    /**
   * Removes all event listeners and nullifies references to prevent memory leaks.
   */
    _cleanup() {
        if (this._stream && this._trackEndedHandler) {
            for (const track of this._stream.getVideoTracks()) {
                track.removeEventListener('ended', this._trackEndedHandler);
            }
        }
        this._trackEndedHandler = null;
        this._videoElement = null;
        this._popupWindow = null;
        this._stream = null;
    }
    /**
   * Emits an event to all registered listeners.
   */
    _emit(event, data) {
        const listeners = this._eventListeners.get(event);
        if (listeners) {
            for (const callback of listeners) {
                try {
                    callback(data);
                }
                catch (e) {
                    logger.error(`Event listener error for "${event}":`, e);
                }
            }
        }
    }
    /**
   * Escapes HTML special characters to prevent XSS in the popup window title.
   */
    _escapeHtml(text) {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}
let singletonInstance = null;
/**
 * Returns the singleton PresentationWindow instance.
 * Ensures only one presentation window exists at a time.
 */
export function getPresentationWindow() {
    if (!singletonInstance) {
        singletonInstance = new PresentationWindow();
    }
    return singletonInstance;
}
/**
 * Opens a presentation window with the provided stream.
 * Shorthand for getPresentationWindow().open(stream, options).
 *
 * @param {MediaStream} stream - The MediaStream to render.
 * @param {IPresentationWindowOptions} [options] - Window configuration.
 * @returns {Window | null}
 */
export function openPresentationWindow(stream, options) {
    return getPresentationWindow().open(stream, options);
}
/**
 * Closes the presentation window if open.
 * Shorthand for getPresentationWindow().close().
 */
export function closePresentationWindow() {
    getPresentationWindow().close();
}
/**
 * Registers a callback for when the presentation window is closed by the user.
 *
 * @param {PresentationWindowEventCallback<void>} callback - The callback to invoke on close.
 */
export function onWindowClosed(callback) {
    getPresentationWindow().onWindowClosed(callback);
}
/**
 * Extracts a MediaStream from a track object (Jitsi/MeetHour wrapper).
 * Supports both raw MediaStream and wrapped track objects with mhTrack/jitsiTrack.
 *
 * @param {Object} track - A track object or raw MediaStream.
 * @returns {MediaStream | null} The extracted MediaStream, or null.
 */
export function getStreamFromTrack(track) {
    if (!track) {
        return null;
    }
    if (track instanceof MediaStream) {
        return track;
    }
    if (track.stream instanceof MediaStream) {
        return track.stream;
    }
    if (track.mhTrack && typeof track.mhTrack.getOriginalStream === 'function') {
        return track.mhTrack.getOriginalStream();
    }
    if (track.jitsiTrack
        && typeof track.jitsiTrack.getOriginalStream === 'function') {
        return track.jitsiTrack.getOriginalStream();
    }
    return null;
}
